import { useEffect, useState } from 'react'
import tw, { css, styled } from 'twin.macro'
import Button from '../Button'
import type { TIconsContainerProps, TShareButtonProps } from './interface'

const generateUtmTags = (source: string, medium: string, campaign = 'sharing_tool') =>
  `?utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`

export const IconsContainer = (props: TIconsContainerProps) => {
  const { url, isOpen, onIconClick } = props
  const [windowLocation, setWindowLocation] = useState<string>()
  const [windowTitle, setWindowTitle] = useState<string>()

  useEffect(() => {
    setWindowLocation(window.location.href)
    setWindowTitle(window.document.title)
  }, [])

  const getShareUrl = (media: string, medium: string) => {
    const currentURL = url ?? windowLocation
    const utm = generateUtmTags(media, medium)
    const fullUrl = `${currentURL}${utm}`
    switch (media) {
      case 'twitter':
        return `https://twitter.com/intent/tweet?text=${fullUrl}`
      case 'facebook':
        return `https://www.facebook.com/sharer.php?u=${fullUrl}&t=${windowTitle}`
      case 'mailto':
        return `mailto:?to=&body=${windowTitle}%20${fullUrl}`
      case 'link':
        return currentURL
      default:
        return ''
    }
  }

  return (
    <div
      id="share-buttons"
      role="listbox"
      css={[
        tw`relative transform -translate-x-1 opacity-0 invisible transition duration-300 flex items-center flex-col p-0 bg-primary-1 pt-14 pb-0 [width:calc(100% + .5rem)] [margin-top:-3.25rem] z-0 rounded-b-full rounded-t-full`,
        isOpen && tw`opacity-100 visible`,
      ]}
    >
      <Button
        as="a"
        handlePress={() => onIconClick?.('facebook')}
        buttonStyle="default"
        variant="primary"
        icon="Facebook"
        aria-label="Share on Facebook"
        target="_blank"
        rel="noopener noreferrer"
        href={getShareUrl('facebook', 'social')}
        backgroundColor="#83DE8"
        tw="w-full text-black rounded-none hover:[background-color:#5A9EDB]"
        role="option"
      />
      <Button
        as="a"
        handlePress={() => onIconClick?.('email')}
        buttonStyle="default"
        variant="primary"
        icon="Mail"
        aria-label="Share by email"
        target="_blank"
        rel="noopener noreferrer"
        href={getShareUrl('mailto', 'social')}
        backgroundColor="#83DE8"
        tw="w-full text-black rounded-none hover:[background-color:#5A9EDB]"
        role="option"
      />
      <Button
        as="a"
        handlePress={() => {
          onIconClick?.('universal-link')
          const articleUrl = getShareUrl('link', 'social')
          if (articleUrl) {
            navigator.clipboard.writeText(articleUrl)
            alert('Link copied to clipboard')
          }
        }}
        buttonStyle="default"
        variant="primary"
        icon="Link"
        aria-label="Share the link of this page"
        target="_blank"
        rel="noopener noreferrer"
        backgroundColor="#83DE8"
        tw="w-full text-black rounded-t-none hover:[background-color:#5A9EDB] cursor-pointer"
        role="option"
      />
    </div>
  )
}

const StyledShareButtonContainer = styled.div(() => [
  css`
    @media print {
      display: none;
    }
  `,
])

const ShareButton = ({ url, text, onShare, ...rest }: TShareButtonProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleClick = () => {
    setIsOpen(!isOpen)
  }

  return (
    <StyledShareButtonContainer tw="rounded-full p-2 w-max">
      <Button
        tw="relative z-10"
        icon="Share"
        aria-label="Expand the sharing options"
        aria-haspopup="listbox"
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-controls="share-buttons"
        handlePress={handleClick}
        css={[
          tw`hover:![background-color:#5A9EDB] text-black`,
          !isOpen ? tw`!bg-primary-1` : tw`![background-color:#5A9EDB]`,
        ]}
        {...rest}
      />
      <IconsContainer onIconClick={onShare} url={url} text={text} isOpen={isOpen} />
    </StyledShareButtonContainer>
  )
}
export default ShareButton
